/**
 * Ask Tracer Component
 * 
 * AI-powered analysis assistant for OSINT Explorer including:
 * - Location pattern analysis
 * - Dwell time analysis
 * - Device comparison
 * - Route pattern detection
 * - Co-location analysis
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Chip,
  Button,
  Stack,
  Menu,
  MenuItem,
  ListItemText,
  alpha,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Psychology as AskIcon,
  Send as SendIcon,
  Lightbulb as PromptIcon,
  Place as PlaceIcon,
  Route as RouteIcon,
  Compare as CompareIcon,
  HelpOutline as HelpIcon,
  Close as CloseIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  FilterAlt as FilterIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  Flag as FlagIcon,
  Warning as WarningIcon,
  CheckCircle as QualityIcon,
  Speed as AccuracyIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { PathfinderDataPoint } from '../../services/pathfinderApi';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: TracerAction[];
}

export type TracerActionType = 
  | 'zoom_to_location'     // Zoom to specific location
  | 'highlight_device'     // Highlight a device on map
  | 'filter_time'          // Filter by time range
  | 'show_dwells'          // Show dwell locations
  | 'compare_devices'      // Compare two devices
  | 'clear_highlights';    // Clear all highlights

export interface TracerAction {
  type: TracerActionType;
  deviceId?: string;
  location?: [number, number];
  timeRange?: { start: string; end: string };
  label: string;
  icon?: React.ReactNode;
}

interface AskTracerProps {
  dataPoints: PathfinderDataPoint[];
  dwellLocations: any[];
  selectedStudents?: string[];
  timeRange?: { start: Date; end: Date } | null;
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
  onHighlightDevice?: (deviceId: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** When true, renders as a full panel without the collapsible header */
  isFullPanel?: boolean;
}

// Suggested prompts for Tracer location analysis
const SUGGESTED_PROMPTS = [
  {
    category: 'Getting Started',
    icon: <HelpIcon fontSize="small" />,
    prompts: [
      'What can you help with?',
      'Summarize the loaded data',
      'How do I use the OSINT Explorer?',
      'Explain the map features',
    ]
  },
  {
    category: 'Dwell Analysis',
    icon: <PlaceIcon fontSize="small" />,
    prompts: [
      'Where did they spend the most time?',
      'What are the top dwell locations?',
      'Are there any unusual dwelling places?',
      'What locations were visited at night?',
      'Identify potential home and work locations',
    ]
  },
  {
    category: 'Movement Patterns',
    icon: <RouteIcon fontSize="small" />,
    prompts: [
      'Describe the movement patterns',
      'What routes are traveled most often?',
      'Are there any gaps in the location data?',
      'What is the typical travel range?',
      'Identify any unusual movements',
    ]
  },
  {
    category: 'Time Analysis',
    icon: <TimelineIcon fontSize="small" />,
    prompts: [
      'What are the activity patterns by time?',
      'When is the device most active?',
      'Compare weekend vs weekday activity',
      'What times do they usually travel?',
      'Is there nighttime activity?',
    ]
  },
  {
    category: 'Multi-Device',
    icon: <CompareIcon fontSize="small" />,
    prompts: [
      'Compare all loaded devices',
      'Were any devices at the same location?',
      'Find co-location events',
      'Which devices have similar patterns?',
      'When and where did devices meet?',
    ]
  },
  {
    category: 'Overwatch Intel',
    icon: <FilterIcon fontSize="small" />,
    prompts: [
      'What news is happening in this area?',
      'Are there any security concerns nearby?',
      'Find recent crime reports in the operation area',
      'What is the operational environment like?',
      'Show relevant intelligence for these locations',
    ]
  },
  {
    category: 'Intel Packet',
    icon: <FlagIcon fontSize="small" />,
    prompts: [
      'What is the Intel Packet?',
      'How do I add notes to the intelligence log?',
      'How do I attach documents to notes?',
      'How does AI document processing work?',
      'What is Pattern of Life (PoL) analysis?',
    ]
  },
];

// Complete App Features list
const APP_FEATURES = [
  {
    category: 'Data Discovery',
    features: [
      { name: 'Area Search', desc: 'Draw circles/polygons on map to find devices in a geographic area' },
      { name: 'ID/IP Lookup', desc: 'Search for specific device registration IDs or IP addresses' },
      { name: 'File Upload', desc: 'Import CSV, GeoJSON, or KML files with location data' },
      { name: 'Watchlist Management', desc: 'Save devices of interest for quick access and tracking' },
    ]
  },
  {
    category: 'Map Visualization',
    features: [
      { name: 'Multi-layer Map', desc: 'Satellite, street, and terrain base maps with multiple overlays' },
      { name: 'Heatmap View', desc: 'Density visualization of location data concentration' },
      { name: 'Route Display', desc: 'Chronological path visualization with color-coded routes' },
      { name: 'Dwell Markers', desc: 'Visual indicators for significant stop locations' },
      { name: 'Cluster Analysis', desc: 'Automatic grouping of nearby data points' },
    ]
  },
  {
    category: 'Pattern of Life (PoL)',
    features: [
      { name: 'Dwell Detection', desc: 'Identify locations where devices stopped for significant time' },
      { name: 'Home/Work Inference', desc: 'Automatically identify likely home and work locations' },
      { name: 'Routine Analysis', desc: 'Discover daily and weekly movement patterns' },
      { name: 'Time-of-Day Analysis', desc: 'Activity patterns by time periods' },
      { name: 'Auto-Intel Generation', desc: 'Automatic creation of PoL notes for significant events' },
    ]
  },
  {
    category: 'Multi-Device Analysis',
    features: [
      { name: 'Co-Traveler Detection', desc: 'Find devices that traveled together' },
      { name: 'Co-Location Events', desc: 'Identify when and where devices were at the same place' },
      { name: 'Device Comparison', desc: 'Side-by-side analysis of multiple device patterns' },
      { name: 'Repeated Visit Sites', desc: 'Locations visited by multiple devices or multiple times' },
    ]
  },
  {
    category: 'Intel Packet',
    features: [
      { name: 'Intelligence Log', desc: 'Centralized view of all notes and OSINT intel' },
      { name: 'Manual Notes', desc: 'Add analyst observations and assessments' },
      { name: 'Auto-Generated PoL Notes', desc: 'System-generated notes for dwells, co-locations, and repeated visits' },
      { name: 'Document Attachments', desc: 'Attach PDFs, images, and documents to notes' },
      { name: 'AI Document Processing', desc: 'Azure AI extracts text and generates insights from attachments' },
      { name: 'Export Options', desc: 'Export intelligence as JSON, CSV, Markdown, or PDF' },
      { name: 'Filtering & Search', desc: 'Filter by type, category, device, and loaded map data' },
    ]
  },
  {
    category: 'Story Mode',
    features: [
      { name: 'Timeline Playback', desc: 'Animated replay of device movements over time' },
      { name: 'Speed Control', desc: 'Adjust playback speed for detailed or quick review' },
      { name: 'Event Highlighting', desc: 'Visual emphasis on significant events during playback' },
    ]
  },
  {
    category: 'Interactive Charts',
    features: [
      { name: 'Activity Timeline', desc: 'Temporal distribution of location signals' },
      { name: 'Day-of-Week Analysis', desc: 'Compare activity across different days' },
      { name: 'Hour-of-Day Patterns', desc: 'Activity distribution throughout the day' },
      { name: 'Location Categories', desc: 'Breakdown of time spent at different location types' },
    ]
  },
  {
    category: 'Overwatch Intel',
    features: [
      { name: 'News Integration', desc: 'Relevant news articles for the operational area' },
      { name: 'Perplexity AI Search', desc: 'AI-powered web search for contextual intelligence' },
      { name: 'Location-Based Intel', desc: 'Intelligence gathered based on device locations' },
    ]
  },
  {
    category: 'AI Assistant (Ask Sentinel)',
    features: [
      { name: 'Natural Language Queries', desc: 'Ask questions about the data in plain English' },
      { name: 'Data Summarization', desc: 'Automatic summaries of loaded device data' },
      { name: 'Pattern Insights', desc: 'AI-generated observations about movement patterns' },
      { name: 'Prompt Library', desc: 'Pre-built questions for common analysis tasks' },
    ]
  },
  {
    category: 'Data Quality',
    features: [
      { name: 'Forensic Flags', desc: 'Data quality indicators (spoofed, low accuracy, etc.)' },
      { name: 'Accuracy Levels', desc: 'High/Moderate/Low accuracy classification' },
      { name: 'Anomaly Detection', desc: 'Flag suspicious or impossible movements' },
    ]
  },
];

// Forensic Flag definitions
const INFORMATIONAL_FLAGS = [
  { bit: 8, name: 'TOO_MANY_DEVICES_AT_LOCATION', desc: 'Many devices with same lat/lon. May warrant additional analysis.', color: '#2196f3' },
  { bit: 10, name: 'RADIO_DERIVED', desc: 'Location from cell tower triangulation vs GPS/WiFi.', color: '#ff9800' },
  { bit: 12, name: 'EU', desc: 'Device spends majority of time in EU.', color: '#9c27b0' },
  { bit: 14, name: 'LIKELY_DRIVING', desc: 'Movement consistent with vehicle/train travel.', color: '#4caf50' },
  { bit: 15, name: 'HIGH_ACCURACY', desc: 'Accuracy: 0-35 meters.', color: '#4caf50' },
  { bit: 16, name: 'MODERATE_ACCURACY', desc: 'Accuracy: 50-220 meters.', color: '#ff9800' },
  { bit: 17, name: 'LOW_ACCURACY', desc: 'Accuracy: 250-10,000 meters.', color: '#f44336' },
  { bit: 19, name: 'MOBILE_NETWORK', desc: 'Signal gathered on mobile network (U.S. only).', color: '#2196f3' },
  { bit: 25, name: 'US', desc: 'Device spends majority of time in U.S.', color: '#1976d2' },
  { bit: 32, name: 'IP_DERIVED', desc: 'Location from IP geolocation, not GPS.', color: '#ff9800' },
];

const QUALITY_FLAGS = [
  { bit: 7, name: 'LAT_GRID_LOCATION', desc: 'Location on grid coordinates. Likely estimated, low precision.', color: '#f44336', filter: true },
  { bit: 9, name: 'SPOOF_LOCATION', desc: 'Location appears spoofed. Filter for analytics.', color: '#d32f2f', filter: true },
  { bit: 13, name: 'OVER_CAPACITY_DEVICE', desc: 'Suspicious device with abnormal signal density.', color: '#d32f2f', filter: true },
  { bit: 20, name: 'HYPERV_OUTSIDE_CLUSTER', desc: 'Moved too fast outside spatial-temporal cluster.', color: '#ff5722' },
  { bit: 21, name: 'HYPERV_WITHIN_CLUSTER', desc: 'Moved too fast within cluster.', color: '#ff5722' },
  { bit: 22, name: 'HYPERV_CLUSTER_INTERLEAVE', desc: 'Clusters far apart overlap in time.', color: '#ff5722' },
  { bit: 23, name: 'TIMESHIFT', desc: 'Observation may be shifted in time.', color: '#ff9800' },
  { bit: 27, name: 'IMPLAUSIBLE_MOVEMENT', desc: 'Impossible movement pattern detected.', color: '#d32f2f', filter: true },
  { bit: 31, name: 'DENSE_CIRCULAR_AREAS', desc: 'Signals form distinct circle on map.', color: '#ff5722' },
];

const ACCURACY_TABLE = [
  { low: 0, mod: 0, high: 0, result: 'Unavailable (>10,000m)' },
  { low: 1, mod: 0, high: 0, result: 'Low (250-10,000m)' },
  { low: 1, mod: 1, high: 0, result: 'Moderate-Low (220-250m)' },
  { low: 0, mod: 1, high: 0, result: 'Moderate (50-220m)' },
  { low: 0, mod: 1, high: 1, result: 'Moderate-High (35-50m)' },
  { low: 0, mod: 0, high: 1, result: 'High (0-35m)' },
];

// Utility functions
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const AskTracer: React.FC<AskTracerProps> = ({
  dataPoints,
  dwellLocations,
  onZoomToLocation,
  onHighlightDevice,
  collapsed = false,
  onToggleCollapse,
  isFullPanel = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;

  // Chat state
  const [isLoading, setIsLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [promptMenuAnchor, setPromptMenuAnchor] = useState<null | HTMLElement>(null);
  const promptMenuOpen = Boolean(promptMenuAnchor);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalPoints = dataPoints.length;
    const totalDwells = dwellLocations.length;
    
    // Group by device
    const deviceIds = new Set<string>();
    dataPoints.forEach(p => {
      deviceIds.add(p.deviceId || p.studentId || 'unknown');
    });
    const totalDevices = deviceIds.size;

    // Time range
    let minTime = Infinity, maxTime = -Infinity;
    dataPoints.forEach(p => {
      const ts = new Date(p.timestamp).getTime();
      if (ts < minTime) minTime = ts;
      if (ts > maxTime) maxTime = ts;
    });
    const startTime = dataPoints.length > 0 ? new Date(minTime) : null;
    const endTime = dataPoints.length > 0 ? new Date(maxTime) : null;

    // Calculate total dwell time
    const totalDwellSeconds = dwellLocations.reduce((sum, d) => sum + (d.duration_seconds || 0), 0);

    return {
      totalPoints,
      totalDwells,
      totalDevices,
      startTime,
      endTime,
      totalDwellSeconds,
      deviceIds: Array.from(deviceIds),
    };
  }, [dataPoints, dwellLocations]);

  // Build context object for API call
  const buildContext = useCallback(() => {
    // Build devices info
    const deviceMap: Record<string, { pointCount: number; dwellCount: number }> = {};
    dataPoints.forEach(p => {
      const id = p.deviceId || p.studentId || 'unknown';
      if (!deviceMap[id]) deviceMap[id] = { pointCount: 0, dwellCount: 0 };
      deviceMap[id].pointCount++;
    });
    dwellLocations.forEach(d => {
      const id = d.entity_id || d.device_id || 'unknown';
      if (deviceMap[id]) deviceMap[id].dwellCount++;
    });

    const devices = Object.entries(deviceMap).map(([id, stats]) => ({
      id,
      pointCount: stats.pointCount,
      dwellCount: stats.dwellCount,
    }));

    return {
      summary: {
        totalPoints: summary.totalPoints,
        totalDevices: summary.totalDevices,
        totalDwells: summary.totalDwells,
        totalDwellSeconds: summary.totalDwellSeconds,
        startTime: summary.startTime?.toISOString() || null,
        endTime: summary.endTime?.toISOString() || null,
      },
      devices,
      dwellLocations: dwellLocations.slice(0, 50).map(d => ({
        latitude: d.latitude,
        longitude: d.longitude,
        duration_seconds: d.duration_seconds,
        start_time: d.start_time,
        place_name: d.place_name || d.location_name,
        entity_id: d.entity_id || d.device_id,
      })),
    };
  }, [dataPoints, dwellLocations, summary]);

  // Call the Tracer AI API
  const callTracerAPI = useCallback(async (question: string): Promise<{ content: string; actions: TracerAction[] }> => {
    try {
      const context = buildContext();
      
      const response = await fetch('/api/tracer-chat/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question, context }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Parse any actions from the response
      const actions: TracerAction[] = data.actions || [];
      
      // Add zoom actions for dwells mentioned in response
      if (data.answer.includes('location') || data.answer.includes('dwell')) {
        dwellLocations.slice(0, 3).forEach((dwell, idx) => {
          if (dwell.latitude && dwell.longitude) {
            actions.push({
              type: 'zoom_to_location',
              location: [dwell.latitude, dwell.longitude],
              label: `Zoom to #${idx + 1}`,
              icon: <VisibilityIcon sx={{ fontSize: 14 }} />,
            });
          }
        });
      }

      return {
        content: data.answer,
        actions,
      };
    } catch (error) {
      console.error('OSINT Explorer API error:', error);
      // Fall back to local analysis
      return generateLocalResponse(question);
    }
  }, [buildContext, dwellLocations]);

  // Local fallback response generator
  const generateLocalResponse = useCallback((userQuestion: string): { content: string; actions: TracerAction[] } => {
    const q = userQuestion.toLowerCase();
    const actions: TracerAction[] = [];
    let response = '';

    // How to use / help questions
    if (q.includes('help') || q.includes('what can you') || (q.includes('how') && (q.includes('use') || q.includes('work')))) {
      response = `## 📡 **Welcome to the OSINT Explorer!**\n\n`;
      response += `The OSINT Explorer is a location intelligence tool for analyzing device movements, identifying significant locations, and discovering relationships.\n\n`;
      response += `---\n\n`;
      response += `### 🚀 **Getting Started**\n\n`;
      response += `**Load Data** using one of these methods:\n`;
      response += `- **Area Search**: Draw a circle/polygon on the map to find devices in an area\n`;
      response += `- **ID/IP Lookup**: Enter a specific device registration ID\n`;
      response += `- **Upload Data**: Import CSV, GeoJSON, or KML files\n\n`;
      response += `---\n\n`;
      response += `### 🔍 **Key Analysis Features**\n\n`;
      response += `| Tab | What It Does |\n`;
      response += `|-----|---------------|\n`;
      response += `| **Data Discovery** | Search & import device data |\n`;
      response += `| **Story Mode** | Chronological movement playback |\n`;
      response += `| **Interactive Charts** | Activity by time, day, location |\n`;
      response += `| **Co-Traveler** | Find devices that traveled together |\n`;
      response += `| **PoL Analysis** | Pattern of Life - routines & habits |\n`;
      response += `| **Dwell Analysis** | Find significant stops (home, work) |\n`;
      response += `| **Intel Packet** | Intelligence log with notes & OSINT |\n\n`;
      response += `---\n\n`;
      response += `### 📋 **Intel Packet Features**\n\n`;
      response += `- **Auto-Generated PoL Notes**: Dwells, co-locations, and repeated visits are automatically logged\n`;
      response += `- **Manual Notes**: Add your own observations and assessments\n`;
      response += `- **Document Attachments**: Attach PDFs, images, and documents to notes\n`;
      response += `- **AI Document Processing**: Azure AI extracts text and generates insights\n`;
      response += `- **Export Options**: JSON, CSV, Markdown, and PDF export\n\n`;
      response += `---\n\n`;
      response += `### 💡 **Tips**\n\n`;
      response += `- **Run Dwell Detection** to find where devices stopped\n`;
      response += `- **Use the timeline slider** to focus on specific time periods\n`;
      response += `- **Compare devices** by loading them into separate tabs\n`;
      response += `- **Attach documents** to notes for AI-powered insights\n`;
      response += `- **Click the 💡 button** above for suggested questions!\n\n`;
      response += `---\n\n`;
      response += `### ❓ **Try Asking**\n\n`;
      response += `- "Summarize the location data"\n`;
      response += `- "Where did this device spend the most time?"\n`;
      response += `- "What are the movement patterns?"\n`;
      response += `- "How do I attach documents to notes?"`;
      return { content: response, actions };
    }

    // Intel Packet questions
    if (q.includes('intel packet') || q.includes('intelligence log') || q.includes('attach') || q.includes('document') || q.includes('pol') || q.includes('pattern of life') || q.includes('what is the intel') || q.includes('what is intel')) {
      response = `## 📋 **Intel Packet & Document Processing**\n\n`;
      response += `The Intel Packet is your centralized intelligence log for managing all notes and OSINT data.\n\n`;
      response += `---\n\n`;
      response += `### 📝 **Note Types**\n\n`;
      response += `- **OVERWATCH**: System-generated OSINT intelligence\n`;
      response += `- **USER**: Manually added analyst notes\n`;
      response += `- **POL**: Auto-generated Pattern of Life notes (dwells, co-locations, repeated visits)\n\n`;
      response += `---\n\n`;
      response += `### 📎 **Document Attachments**\n\n`;
      response += `1. **Attach Files**: Click "Attach File" on any note to upload documents\n`;
      response += `2. **Supported Formats**: PDF, Word, Excel, images (JPG, PNG, etc.)\n`;
      response += `3. **AI Processing**: Click the ✨ button to extract text and generate insights\n`;
      response += `4. **Azure AI Services**: Document Intelligence for structured documents, Computer Vision for images\n\n`;
      response += `---\n\n`;
      response += `### 🔄 **Auto-Generated PoL Notes**\n\n`;
      response += `When you load device data, the system automatically creates notes for:\n`;
      response += `- **Significant Dwells**: Stops over 30 minutes at a location\n`;
      response += `- **Co-Locations**: When multiple tracked devices meet\n`;
      response += `- **Repeated Visits**: Locations visited multiple times\n\n`;
      response += `These notes include confidence levels and detailed location/time information.`;
      return { content: response, actions };
    }

    // Features and functionality questions
    if (q.includes('feature') || q.includes('capabilit') || q.includes('functionality') || q.includes('what does') || q.includes('what can OSINT Explorer')) {
      response = `## 📡 **The OSINT Explorer Features**\n\n`;
      response += `The OSINT Explorer is a comprehensive location intelligence platform. Here are the key capabilities:\n\n`;
      response += `---\n\n`;
      response += `### 🔍 **Data Discovery**\n`;
      response += `- Area Search (draw on map)\n`;
      response += `- Device ID/IP Lookup\n`;
      response += `- File Upload (CSV, GeoJSON, KML)\n`;
      response += `- Watchlist Management\n\n`;
      response += `### 🗺️ **Map Visualization**\n`;
      response += `- Multi-layer maps (satellite, street, terrain)\n`;
      response += `- Heatmaps, routes, and dwell markers\n`;
      response += `- Cluster analysis\n\n`;
      response += `### 📊 **Pattern of Life (PoL)**\n`;
      response += `- Dwell detection\n`;
      response += `- Home/work inference\n`;
      response += `- Auto-intel generation\n\n`;
      response += `### 👥 **Multi-Device Analysis**\n`;
      response += `- Co-traveler detection\n`;
      response += `- Co-location events\n`;
      response += `- Device comparison\n\n`;
      response += `### 📋 **Intel Packet**\n`;
      response += `- Intelligence log with notes\n`;
      response += `- Document attachments\n`;
      response += `- AI-powered document processing\n`;
      response += `- Export (JSON, CSV, Markdown, PDF)\n\n`;
      response += `### 🎬 **Story Mode**\n`;
      response += `- Timeline playback of movements\n\n`;
      response += `### 📈 **Interactive Charts**\n`;
      response += `- Activity analysis by time/day\n\n`;
      response += `### 🔭 **Overwatch Intel**\n`;
      response += `- News integration\n`;
      response += `- Perplexity AI search\n\n`;
      response += `*Click the 💡 button for more specific questions, or check the "App Features" section below!*`;
      return { content: response, actions };
    }

    // Data summary
    if (q.includes('summary') || q.includes('overview') || q.includes('loaded')) {
      response = `📊 **Data Summary**\n\n`;
      if (dataPoints.length === 0) {
        response += `No data currently loaded. Upload a dataset or search for devices to begin analysis.`;
      } else {
        response += `• **Data Points**: ${summary.totalPoints.toLocaleString()}\n`;
        response += `• **Devices**: ${summary.totalDevices}\n`;
        response += `• **Dwell Locations**: ${summary.totalDwells}\n`;
        if (summary.startTime && summary.endTime) {
          response += `• **Time Range**: ${summary.startTime.toLocaleDateString()} - ${summary.endTime.toLocaleDateString()}\n`;
        }
        if (summary.totalDwellSeconds > 0) {
          response += `• **Total Dwell Time**: ${formatDuration(summary.totalDwellSeconds)}\n`;
        }
      }
      return { content: response, actions };
    }

    // Location/dwell questions
    if (q.includes('where') || q.includes('dwell') || q.includes('location') || q.includes('spent') || q.includes('time')) {
      if (dwellLocations.length === 0) {
        response = `📍 **Dwell Analysis**\n\nNo dwell locations detected yet. `;
        response += `Run dwell detection from the "PoL Analysis" panel.`;
      } else {
        const sortedDwells = [...dwellLocations].sort((a, b) => (b.duration_seconds || 0) - (a.duration_seconds || 0));
        const topDwells = sortedDwells.slice(0, 5);
        
        response = `📍 **Top Dwell Locations**\n\n`;
        topDwells.forEach((dwell, idx) => {
          const duration = formatDuration(dwell.duration_seconds || 0);
          const place = dwell.place_name || `(${dwell.latitude?.toFixed(4)}, ${dwell.longitude?.toFixed(4)})`;
          response += `${idx + 1}. **${duration}** at ${place}\n`;
          
          if (dwell.latitude && dwell.longitude) {
            actions.push({
              type: 'zoom_to_location',
              location: [dwell.latitude, dwell.longitude],
              label: `Zoom to #${idx + 1}`,
              icon: <VisibilityIcon sx={{ fontSize: 14 }} />,
            });
          }
        });
      }
      return { content: response, actions };
    }

    // Default
    response = `I'll analyze that for you...\n\n`;
    response += `Currently loaded: ${summary.totalPoints.toLocaleString()} points, ${summary.totalDevices} devices, ${summary.totalDwells} dwells.\n\n`;
    response += `*AI analysis is connecting. Try again in a moment.*`;
    return { content: response, actions };
  }, [dataPoints, dwellLocations, summary]);

  // Generate AI response - calls API or falls back to local
  const generateResponse = useCallback(async (userQuestion: string): Promise<{ content: string; actions: TracerAction[] }> => {
    // Quick local responses for app-specific questions that the API doesn't know about
    const q = userQuestion.toLowerCase();
    
    // Help and usage questions
    if (q.includes('how') && (q.includes('use') || q.includes('help'))) {
      return generateLocalResponse(userQuestion);
    }
    
    // Intel Packet and document-related questions (app-specific features)
    if (q.includes('intel packet') || q.includes('intelligence log') || 
        q.includes('attach') || q.includes('document') || 
        q.includes('pol') || q.includes('pattern of life') ||
        q.includes('what is the intel') || q.includes('what is intel')) {
      return generateLocalResponse(userQuestion);
    }
    
    // App features and capabilities questions
    if (q.includes('feature') || q.includes('capabilit') || q.includes('what can') || 
        q.includes('functionality') || q.includes('what does')) {
      return generateLocalResponse(userQuestion);
    }
    
    // Try API first, fall back to local
    return callTracerAPI(userQuestion);
  }, [callTracerAPI, generateLocalResponse]);

  // Handle sending a question
  const handleSend = useCallback(async (promptOverride?: string) => {
    const textToSend = promptOverride || question;
    if (!textToSend.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: textToSend.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    // Simulate AI thinking delay
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 400));

    const { content, actions } = await generateResponse(userMessage.content);

    const assistantMessage: ChatMessage = {
      role: 'assistant',
      content,
      timestamp: new Date(),
      actions,
    };

    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(false);

    // Scroll to bottom
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [question, isLoading, generateResponse]);

  // Handle action execution
  const handleAction = useCallback((action: TracerAction) => {
    switch (action.type) {
      case 'zoom_to_location':
        if (action.location && onZoomToLocation) {
          onZoomToLocation(action.location[0], action.location[1], 15);
        }
        break;
      case 'highlight_device':
        if (action.deviceId && onHighlightDevice) {
          onHighlightDevice(action.deviceId);
        }
        break;
    }
  }, [onZoomToLocation, onHighlightDevice]);

  const colors = {
    accent: primaryColor,
    text: theme.palette.text.primary,
    textSecondary: theme.palette.text.secondary,
    border: theme.palette.divider,
  };

return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      bgcolor: isFullPanel ? 'transparent' : (isDark ? 'rgba(30, 32, 48, 0.6)' : 'rgba(248, 250, 252, 0.9)'),
      borderRadius: isFullPanel ? 0 : 2,
      border: isFullPanel ? 'none' : `1px solid ${colors.border}`,
      overflow: 'hidden',
      height: isFullPanel ? '100%' : 'auto',
    }}>
      {/* Header - only show when not in full panel mode */}
      {!isFullPanel && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            p: 1.5,
            borderBottom: collapsed ? 'none' : `1px solid ${colors.border}`,
            cursor: 'pointer',
          }}
          onClick={onToggleCollapse}
        >
          <AskIcon sx={{ color: colors.accent, fontSize: '1rem' }} />
          <Typography sx={{ color: colors.text, fontWeight: 600, fontSize: '0.8rem', flex: 1 }}>
            Ask Sentinel
          </Typography>
          
          {/* Clear button */}
          {messages.length > 0 && !collapsed && (
            <Tooltip title="Clear chat">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setMessages([]);
                }}
                sx={{ 
                  color: colors.textSecondary, 
                  p: 0.5,
                  '&:hover': { bgcolor: alpha(colors.accent, 0.1) },
                }}
              >
                <CloseIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Collapse toggle */}
          <IconButton
            size="small"
            sx={{ color: colors.textSecondary, p: 0.5 }}
          >
            {collapsed ? <ExpandIcon sx={{ fontSize: '1rem' }} /> : <CollapseIcon sx={{ fontSize: '1rem' }} />}
          </IconButton>
        </Box>
      )}

      {/* Content area - conditional display based on collapsed state */}
      <Collapse in={isFullPanel || !collapsed} sx={{ flex: isFullPanel ? 1 : 'none', minHeight: 0, overflow: isFullPanel ? 'auto' : 'visible' }}>
        <Box sx={{ p: isFullPanel ? 1 : 1.5, pt: 0, display: 'flex', flexDirection: 'column', height: isFullPanel ? '100%' : 'auto' }}>
          {/* Network Overview Stats */}
          {dataPoints.length > 0 && (
            <Box sx={{ mb: 1.5, pt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text, display: 'block', mb: 0.75 }}>
                Network Overview
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                <Chip 
                  icon={<Box sx={{ display: 'flex', alignItems: 'center' }}>🔌</Box>}
                  label={`${summary.totalDevices} known`}
                  size="small"
                  sx={{ 
                    height: 24, 
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.info.main, 0.15),
                    color: theme.palette.info.main,
                    fontWeight: 600,
                    '& .MuiChip-icon': { ml: 0.5 }
                  }}
                />
                <Chip 
                  icon={<PlaceIcon sx={{ fontSize: 14 }} />}
                  label={`${summary.totalDwells} place`}
                  size="small"
                  sx={{ 
                    height: 24, 
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning.main,
                    fontWeight: 600,
                  }}
                />
                <Chip 
                  icon={<TimelineIcon sx={{ fontSize: 14 }} />}
                  label={`${(summary.totalPoints / 1000).toFixed(1)}k pts`}
                  size="small"
                  sx={{ 
                    height: 24, 
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success.main,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Question Input */}
          <TextField
            fullWidth
            size="small"
            placeholder="Ask about connections, patterns, ..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isLoading) {
                handleSend();
              }
            }}
            disabled={isLoading}
            InputProps={{
              sx: {
                bgcolor: isDark ? 'rgba(20, 22, 35, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                color: colors.text,
                fontSize: '0.8rem',
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.accent },
                '&.Mui-focused fieldset': { borderColor: colors.accent },
              },
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Prompt Library Button */}
                  <Tooltip title="Prompt Library">
                    <IconButton
                      size="small"
                      onClick={(e) => setPromptMenuAnchor(e.currentTarget)}
                      sx={{ 
                        color: promptMenuOpen ? colors.accent : colors.textSecondary,
                        '&:hover': { color: colors.accent },
                      }}
                    >
                      <PromptIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  {/* Send Button */}
                  <IconButton
                    size="small"
                    onClick={() => handleSend()}
                    disabled={isLoading || !question.trim()}
                    sx={{ color: colors.accent }}
                  >
                    {isLoading ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: '1rem' }} />}
                  </IconButton>
                </Box>
              ),
            }}
          />

          {/* Prompt Library Menu */}
          <Menu
            anchorEl={promptMenuAnchor}
            open={promptMenuOpen}
            onClose={() => setPromptMenuAnchor(null)}
            PaperProps={{
              sx: {
                bgcolor: isDark ? 'rgba(30, 32, 48, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                border: `1px solid ${colors.border}`,
                borderRadius: 2,
                maxHeight: 400,
                minWidth: 280,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }
            }}
          >
            <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon sx={{ fontSize: '0.9rem', color: colors.accent }} />
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: colors.accent }}>
                  ? How to Use
                </Typography>
              </Box>
            </Box>
            {SUGGESTED_PROMPTS.map((category, catIdx) => (
              <Box key={catIdx}>
                <Box sx={{ px: 2, py: 0.75, bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {category.icon}
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text }}>
                    {category.category}
                  </Typography>
                </Box>
                {category.prompts.map((prompt, promptIdx) => (
                  <MenuItem
                    key={promptIdx}
                    onClick={() => {
                      setPromptMenuAnchor(null);
                      handleSend(prompt);
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      py: 0.75,
                      px: 2,
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <ListItemText primary={prompt} />
                  </MenuItem>
                ))}
              </Box>
            ))}
          </Menu>

          {/* Chat Area */}
        {messages.length > 0 && (
          <Box sx={{ 
            flex: 1,
            minHeight: 0,
            maxHeight: isFullPanel ? 'none' : 300, 
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 1.5, 
            pb: 1.5,
            mt: 1,
          }}>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: msg.role === 'user' 
                    ? alpha(primaryColor, 0.1)
                    : alpha(theme.palette.grey[500], 0.1),
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {msg.role === 'user' ? 'You' : '📡 Sentinel'}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5,
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    '& strong': { fontWeight: 600 },
                  }}
                >
                  {msg.content}
                </Typography>

                {/* Action Buttons */}
                {msg.actions && msg.actions.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    {msg.actions.map((action, actionIdx) => (
                      <Button
                        key={actionIdx}
                        size="small"
                        variant="outlined"
                        startIcon={action.icon}
                        onClick={() => handleAction(action)}
                        sx={{ 
                          fontSize: '0.65rem', 
                          py: 0.25,
                          px: 1,
                          textTransform: 'none',
                        }}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Analyzing...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}

        {/* Clear chat button for full panel mode */}
        {isFullPanel && messages.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, borderTop: `1px solid ${colors.border}` }}>
            <Button 
              size="small" 
              onClick={() => setMessages([])}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              Clear Chat
            </Button>
          </Box>
        )}

        {/* Info & Forensic Flags - Collapsible sections (only in full panel mode) */}
        {isFullPanel && (
          <Box sx={{ 
            borderTop: `1px solid ${colors.border}`, 
            mt: 1,
          }}>
            {/* About */}
            <Accordion 
              disableGutters 
              elevation={0}
              sx={{ 
                bgcolor: 'transparent', 
                '&:before': { display: 'none' },
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 36, py: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <InfoIcon sx={{ fontSize: 14, color: primaryColor }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>About the OSINT Explorer</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, lineHeight: 1.5 }}>
                  Location intelligence platform for analyzing device movements, identifying significant locations, and discovering device relationships.
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2, fontSize: '0.65rem', color: colors.textSecondary }}>
                  <li>Area & device ID search with file upload support</li>
                  <li>Pattern of Life (PoL) analysis with auto-intel generation</li>
                  <li>Dwell detection & home/work inference</li>
                  <li>Co-traveler & co-location identification</li>
                  <li>Intel Packet with document attachments & AI processing</li>
                  <li>Overwatch intel integration (news, Perplexity AI)</li>
                  <li>Story mode timeline playback</li>
                  <li>Interactive charts & analytics</li>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* App Features */}
            <Accordion 
              disableGutters 
              elevation={0}
              sx={{ 
                bgcolor: 'transparent', 
                '&:before': { display: 'none' },
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 36, py: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RouteIcon sx={{ fontSize: 14, color: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>App Features</Typography>
                  <Chip label={APP_FEATURES.reduce((sum, cat) => sum + cat.features.length, 0)} size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5, bgcolor: alpha('#10b981', 0.15), color: '#10b981' }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, maxHeight: 350, overflow: 'auto' }}>
                {APP_FEATURES.map((category, catIdx) => (
                  <Box key={catIdx} sx={{ mb: 1.5 }}>
                    <Typography sx={{ 
                      fontSize: '0.7rem', 
                      fontWeight: 700, 
                      color: primaryColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 0.5,
                      pb: 0.25,
                      borderBottom: `1px solid ${alpha(primaryColor, 0.2)}`,
                    }}>
                      {category.category}
                    </Typography>
                    {category.features.map((feature, featIdx) => (
                      <Box key={featIdx} sx={{ mb: 0.5, pl: 1 }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: colors.text }}>
                          {feature.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: colors.textSecondary, lineHeight: 1.3 }}>
                          {feature.desc}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Informational Flags */}
            <Accordion 
              disableGutters 
              elevation={0}
              sx={{ 
                bgcolor: 'transparent', 
                '&:before': { display: 'none' },
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 36, py: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FlagIcon sx={{ fontSize: 14, color: '#2196f3' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Informational Flags</Typography>
                  <Chip label={INFORMATIONAL_FLAGS.length} size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, maxHeight: 200, overflow: 'auto' }}>
                {INFORMATIONAL_FLAGS.map((flag) => (
                  <Box key={flag.bit} sx={{ mb: 0.75, pl: 0.5, borderLeft: `2px solid ${flag.color}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={`${flag.bit}`} size="small" sx={{ height: 14, fontSize: '0.55rem', bgcolor: alpha(flag.color, 0.15), color: flag.color }} />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>{flag.name}</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.6rem' }}>{flag.desc}</Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Quality Flags */}
            <Accordion 
              disableGutters 
              elevation={0}
              sx={{ 
                bgcolor: 'transparent', 
                '&:before': { display: 'none' },
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 36, py: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningIcon sx={{ fontSize: 14, color: '#f44336' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Quality Indicators</Typography>
                  <Chip label={QUALITY_FLAGS.length} size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 0.5, bgcolor: alpha('#f44336', 0.15), color: '#f44336' }} />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.6rem', display: 'block', mb: 1 }}>
                  Consider filtering these for precise analytics.
                </Typography>
                {QUALITY_FLAGS.map((flag) => (
                  <Box key={flag.bit} sx={{ mb: 0.75, pl: 0.5, borderLeft: `2px solid ${flag.color}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={`${flag.bit}`} size="small" sx={{ height: 14, fontSize: '0.55rem', bgcolor: alpha(flag.color, 0.15), color: flag.color }} />
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>{flag.name}</Typography>
                      {flag.filter && <Chip label="FILTER" size="small" sx={{ height: 12, fontSize: '0.5rem', bgcolor: '#d32f2f', color: 'white', ml: 'auto' }} />}
                    </Box>
                    <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.6rem' }}>{flag.desc}</Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Accuracy Reference */}
            <Accordion 
              disableGutters 
              elevation={0}
              sx={{ 
                bgcolor: 'transparent', 
                '&:before': { display: 'none' },
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 36, py: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccuracyIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Accuracy Reference</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.55rem', fontWeight: 600, py: 0.25, px: 0.5 }}>LOW</TableCell>
                        <TableCell sx={{ fontSize: '0.55rem', fontWeight: 600, py: 0.25, px: 0.5 }}>MOD</TableCell>
                        <TableCell sx={{ fontSize: '0.55rem', fontWeight: 600, py: 0.25, px: 0.5 }}>HIGH</TableCell>
                        <TableCell sx={{ fontSize: '0.55rem', fontWeight: 600, py: 0.25, px: 0.5 }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ACCURACY_TABLE.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontSize: '0.55rem', py: 0.25, px: 0.5 }}>
                            <Chip label={row.low} size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: row.low ? alpha('#f44336', 0.15) : 'transparent', color: row.low ? '#f44336' : colors.textSecondary }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.55rem', py: 0.25, px: 0.5 }}>
                            <Chip label={row.mod} size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: row.mod ? alpha('#ff9800', 0.15) : 'transparent', color: row.mod ? '#ff9800' : colors.textSecondary }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.55rem', py: 0.25, px: 0.5 }}>
                            <Chip label={row.high} size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: row.high ? alpha('#4caf50', 0.15) : 'transparent', color: row.high ? '#4caf50' : colors.textSecondary }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.6rem', py: 0.25, px: 0.5 }}>{row.result}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            {/* Best Practices */}
            <Accordion 
              disableGutters 
              elevation={0}
              sx={{ 
                bgcolor: 'transparent', 
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 36, py: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <QualityIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Best Practices</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: '#f44336', display: 'block', mb: 0.5 }}>
                  🚫 Filter for analytics:
                </Typography>
                <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip label="SPOOF_LOCATION" size="small" sx={{ height: 16, fontSize: '0.55rem' }} />
                  <Chip label="OVER_CAPACITY_DEVICE" size="small" sx={{ height: 16, fontSize: '0.55rem' }} />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: '#ff9800', display: 'block', mb: 0.5 }}>
                  ⚠️ Also filter for place visits:
                </Typography>
                <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip label="LAT_GRID_LOCATION" size="small" sx={{ height: 16, fontSize: '0.55rem' }} />
                  <Chip label="LIKELY_DRIVING" size="small" sx={{ height: 16, fontSize: '0.55rem' }} />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: '#4caf50', display: 'block', mb: 0.5 }}>
                  ✅ Useful for commuting:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip label="LIKELY_DRIVING" size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: alpha('#4caf50', 0.15), color: '#4caf50' }} />
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AskTracer;

